import React, { useEffect, useState, createContext, useContext } from 'react';
import { DebugLogEntry, DebugConfig } from './types';
import { getDebugLogs, clearDebugLogs, DEBUG_LOG_EVENT, DEBUG_LOG_CLEAR_EVENT, setMaxLogs } from './store';
interface DebugContextType {
  logs: DebugLogEntry[];
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  clearLogs: () => void;
  config: DebugConfig;
}
const DebugContext = createContext<DebugContextType | null>(null);
export function DebugProvider({
  children,
  config = {}
}: {
  children: React.ReactNode;
  config?: DebugConfig;
}) {
  const [logs, setLogs] = useState<DebugLogEntry[]>(getDebugLogs());
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (config.maxLogs) setMaxLogs(config.maxLogs);
  }, [config.maxLogs]);
  useEffect(() => {
    // Sync with global state on mount
    setLogs([...getDebugLogs()]);
    const handleAdd = () => setLogs([...getDebugLogs()]);
    const handleClear = () => setLogs([]);
    window.addEventListener(DEBUG_LOG_EVENT, handleAdd);
    window.addEventListener(DEBUG_LOG_CLEAR_EVENT, handleClear);
    return () => {
      window.removeEventListener(DEBUG_LOG_EVENT, handleAdd);
      window.removeEventListener(DEBUG_LOG_CLEAR_EVENT, handleClear);
    };
  }, []);
  // Keyboard shortcut to toggle panel (Ctrl+Shift+D or Cmd+Shift+D)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  return <DebugContext.Provider value={{
    logs,
    isOpen,
    setIsOpen,
    clearLogs: clearDebugLogs,
    config
  }} data-id="element-7127">
      {children}
    </DebugContext.Provider>;
}
export function useDebug() {
  const context = useContext(DebugContext);
  if (!context) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
}